import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Tab, Nav, Card, Spinner } from 'react-bootstrap';
import InvestmentCard from './InvestmentCard';
import axios from 'axios';

const InvestmentsPage = () => {
    const [allInvestments, setAllInvestments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchInvestments = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    setError("Vous devez être connecté pour voir vos investissements.");
                    setLoading(false);
                    return;
                }

                const config = {
                    headers: {
                        'Authorization': `Bearer ${token}` 
                    }
                };
                
                const res = await axios.get('/api/investment/me', config); 
                
                setAllInvestments(res.data);
                setLoading(false);

            } catch (err) {
                console.error("Erreur détaillée de l'API :", err.response ? err.response.data : err.message);
                
                if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                     setError('Session expirée. Veuillez vous reconnecter.');
                } else {
                     setError('Erreur lors de la récupération des investissements. Vérifiez la console.');
                }

                setLoading(false);
            }
        };

        fetchInvestments();
    }, []);

    // --- LOGIQUE DE FILTRAGE ---
    const ongoingInvestments = allInvestments.filter(inv => inv.status === 'active' && !inv.chainCompleted);
    const successfulInvestissements = allInvestments.filter(inv => inv.status === 'completed' || inv.chainCompleted);

    const totalInvested = allInvestments.reduce((sum, inv) => 
        sum + (inv.amountInOriginalCurrency ? parseFloat(inv.amountInOriginalCurrency) : 0), 
    0);

    const totalProfit = successfulInvestissements.reduce((sum, inv) => 
        sum + (inv.amountInOriginalCurrency ? parseFloat(inv.amountInOriginalCurrency) * 2 : 0), 
    0);

    // --- RENDU : Gestion des états de chargement/erreur ---

    if (loading) {
        return (
            <Container className="text-center mt-4 pt-4"> 
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Chargement...</span>
                </Spinner>
                <p className="mt-3">Chargement des investissements...</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="text-center mt-5">
                <p className="alert alert-danger">{error}</p>
            </Container>
        );
    }

    // --- RENDU PRINCIPAL ---
    return (
        <Container fluid className="mt-4 py-4">
            
            {/* TITRE PRINCIPAL (Centré) */}
            <Row className="justify-content-center">
                <Col xs={12} lg={6} xl={5} className="mb-4">
                    <h1 className="text-center mb-1 fw-bolder text-primary">Tableau de Bord</h1>
                    <p className="text-center text-muted mb-2">Gérez et suivez la performance de vos actifs.</p>
                </Col>
            </Row>

            {/* SECTION 1: APERÇU DU PORTEFEUILLE (Centré et Limité en Largeur) */}
            <Row className="justify-content-center"> 
                <Col xs={12} lg={8} xl={7}> 
                    <h2 className="mb-2 fs-5 text-secondary pt-2">Aperçu du Portefeuille</h2>
                    
                    <Row className="g-2 mb-4"> 
                        
                        <Col md={4} className="text-center">
                            <Card className="py-1 px-3 bg-light border-0 h-100 border-start border-primary border-4">
                                <h5 className="text-muted fw-normal mb-0 fs-6">Total Investi</h5>
                                <p className="lead fw-bolder text-primary fs-5 mb-1">{totalInvested.toLocaleString()} FCFA</p>
                            </Card>
                        </Col>
                        
                        <Col md={4} className="text-center">
                            <Card className="py-1 px-3 bg-light border-0 h-100 border-start border-success border-4">
                                <h5 className="text-muted fw-normal mb-0 fs-6">Gains Totaux</h5>
                                <p className="lead fw-bolder text-success fs-5 mb-1">{totalProfit.toLocaleString()} FCFA</p>
                            </Card>
                        </Col>
                        
                        <Col md={4} className="text-center">
                            <Card className="py-1 px-3 bg-light border-0 h-100 border-start border-warning border-4">
                                <h5 className="text-muted fw-normal mb-0 fs-6">En Cours</h5>
                                <p className="lead fw-bolder text-warning fs-5 mb-1">{ongoingInvestments.length}</p>
                            </Card>
                        </Col>
                    </Row>
                </Col>
            </Row>

            {/* --- SECTION 2: DÉTAIL DES ACTIFS (Liste d'investissements - OCCUPATION MAXIMALE) --- */}
            <Row className="justify-content-center">
                <Col lg={11} xl={10}>
                    <h2 className="mb-2 fs-5 text-secondary pt-2">Détail des Actifs</h2>

                    <Tab.Container defaultActiveKey="inProgress">
                        <Nav variant="pills" className="justify-content-center mb-3 p-0 bg-white rounded shadow-sm border">
                            <Nav.Item>
                                <Nav.Link eventKey="inProgress" className="px-3 py-2 text-decoration-none bg-primary text-white">
                                    <span className="fw-bold">En cours</span> ({ongoingInvestments.length})
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="success" className="px-3 py-2 text-decoration-none">
                                    <span className="fw-bold">Succès</span> ({successfulInvestissements.length})
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                        
                        <Tab.Content className="mt-3">
                            <Tab.Pane eventKey="inProgress">
                                <Row className="justify-content-start g-3"> 
                                    {ongoingInvestments.length > 0 ? (
                                        ongoingInvestments.map(inv => (
                                            // Code propre sans commentaire mal placé
                                            <Col sm={6} key={inv._id}>
                                                <InvestmentCard investment={inv} />
                                            </Col>
                                        ))
                                    ) : (
                                        <Col xs={12}><p className="text-center text-muted p-4 border rounded bg-white">Aucun investissement en cours.</p></Col>
                                    )}
                                </Row>
                            </Tab.Pane>
                            
                            <Tab.Pane eventKey="success">
                                <Row className="justify-content-start g-3"> 
                                    {successfulInvestissements.length > 0 ? (
                                        successfulInvestissements.map(inv => (
                                            // Code propre sans commentaire mal placé
                                            <Col sm={6} key={inv._id}>
                                                <InvestmentCard investment={inv} />
                                            </Col>
                                        ))
                                    ) : (
                                        <Col xs={12}><p className="text-center text-muted p-4 border rounded bg-white">Aucun investissement réussi.</p></Col>
                                    )}
                                </Row>
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </Col> 
            </Row> 
        </Container>
    );
};

export default InvestmentsPage;